import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import TrialExpiredGate from './components/TrialExpiredGate';
import { initOneSignal } from './lib/onesignal';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LanguageSelectPage from './pages/LanguageSelectPage';
import SetupProfilePage from './pages/SetupProfilePage';
import SignaturePage from './pages/SignaturePage';
import DashboardPage from './pages/DashboardPage';
import NewContractPage from './pages/NewContractPage';
import ContractViewPage from './pages/ContractViewPage';
import GuestSignPage from './pages/GuestSignPage';
import GuestCheckinPage from './pages/GuestCheckinPage';
import NotificationsPage from './pages/NotificationsPage';
import TemplatesPage from './pages/TemplatesPage';
import PropertiesPage from './pages/PropertiesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import LandingPage from './pages/LandingPage';
import EventiPage from './pages/EventiPage';
import SeoLandingPage from './pages/seo/SeoLandingPage';
import { seoSlugs } from './pages/seo/seoPagesData';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/CookiePolicyPage.jsx';
import GuidesHubPage from './pages/GuidesHubPage.jsx';
import GuideContrattoLocazione from './pages/guides/GuideContrattoLocazione.jsx';
import GuideContabilita from './pages/guides/GuideContabilita.jsx';
import GuidePrezzi from './pages/guides/GuidePrezzi.jsx';
import GuideBurocrazia from './pages/guides/GuideBurocrazia.jsx';
import GuideEventi from './pages/guides/GuideEventi.jsx';
import PublicStatsPage from './pages/PublicStatsPage';
import SubscribePage from './pages/SubscribePage';
import StatisticsPage from './pages/StatisticsPage';
import AdminPage from './pages/AdminPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ReceiptsPage from './pages/ReceiptsPage';
import ContactPage from './pages/ContactPage';
import ContabilitaPage from './pages/ContabilitaPage';
import SchedinePage from './pages/SchedinePage';
import ImpostaSoggiornoPage from './pages/ImpostaSoggiornoPage';
import TouristTaxReceiptsPage from './pages/TouristTaxReceiptsPage';
import CleaningPage from './pages/CleaningPage.jsx';
import StaffCleaningPage from './pages/StaffCleaningPage.jsx';
import HostCleaningAppPage from './pages/HostCleaningAppPage.jsx';
import QRCodePage from './pages/QRCodePage.jsx';
import CookieBanner from './components/CookieBanner.jsx';
import InstallBanner from './components/InstallBanner';
import AppLayout from './components/layout/AppLayout';

// Remove external badge
function useRemoveBadge() {
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(a => a.remove());
    };
    remove();
    const iv = setInterval(remove, 300);
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(iv); obs.disconnect(); };
  }, []);
}

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-zinc-500">Caricamento...</p>
    </div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Route that redirects to appropriate page based on user state
const SmartRoute = ({ children, bare = false }) => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin bypasses profile setup
  if (user.role === 'admin') {
    return bare ? children : <AppLayout>{children}</AppLayout>;
  }

  // If user hasn't selected language yet
  if (!user.preferred_language && !user.owner_profile_complete) {
    return <Navigate to="/select-language" replace />;
  }

  // If user has language but no profile
  if (!user.owner_profile_complete) {
    return <Navigate to="/setup-profile" replace />;
  }

  // Subscription expired / trial ended: show blocking gate on top of page
  // Allow /subscription and /subscription/success pages to work (checkout flow)
  const isActive = user.subscription?.is_active !== false;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const allowedWhenExpired = path.startsWith('/subscription') || path.startsWith('/subscribe');
  if (!isActive && !allowedWhenExpired) {
    return (
      <>
        {bare ? children : <AppLayout>{children}</AppLayout>}
        <TrialExpiredGate user={user} onLogout={logout} />
      </>
    );
  }

  return bare ? children : <AppLayout>{children}</AppLayout>;
};

// Auth routes (redirect to dashboard if already logged in)
const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    if (user.role === 'admin' || (!user.preferred_language || !user.owner_profile_complete)) {
      if (user.role === 'admin') return <Navigate to="/dashboard" replace />;
      return <Navigate to="/select-language" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      {seoSlugs.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<SeoLandingPage slug={slug} />} />
      ))}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookie" element={<CookiePolicyPage />} />

      {/* GEO/SEO long-form guides (rich content for LLM citations and Google) */}
      <Route path="/guide" element={<GuidesHubPage />} />
      <Route path="/guide/contratto-locazione-turistica" element={<GuideContrattoLocazione />} />
      <Route path="/guide/contabilita-affitti-brevi" element={<GuideContabilita />} />
      <Route path="/guide/prezzi-airbnb-booking" element={<GuidePrezzi />} />
      <Route path="/guide/burocrazia-host-affitti-brevi" element={<GuideBurocrazia />} />
      <Route path="/guide/eventi-affitti-brevi" element={<GuideEventi />} />
      <Route path="/stats" element={<PublicStatsPage />} />
      <Route path="/host-italia-in-numeri" element={<PublicStatsPage />} />
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/guest/:token" element={<GuestSignPage />} />
      <Route path="/checkin/:token" element={<GuestCheckinPage />} />

      {/* Protected routes */}
      <Route path="/select-language" element={<ProtectedRoute><LanguageSelectPage /></ProtectedRoute>} />
      <Route path="/setup-profile" element={<ProtectedRoute><SetupProfilePage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<SmartRoute><DashboardPage /></SmartRoute>} />
      <Route path="/new-contract" element={<SmartRoute><NewContractPage /></SmartRoute>} />
      <Route path="/contract/:id" element={<SmartRoute><ContractViewPage /></SmartRoute>} />
      <Route path="/notifications" element={<SmartRoute><NotificationsPage /></SmartRoute>} />
      <Route path="/templates" element={<SmartRoute><TemplatesPage /></SmartRoute>} />
      <Route path="/properties" element={<SmartRoute><PropertiesPage /></SmartRoute>} />
      <Route path="/firma" element={<SmartRoute><SignaturePage /></SmartRoute>} />
      <Route path="/subscription" element={<SmartRoute><SubscriptionPage /></SmartRoute>} />
      <Route path="/subscription/success" element={<SmartRoute bare><SubscriptionSuccessPage /></SmartRoute>} />
      <Route path="/subscribe" element={<SmartRoute bare><SubscribePage /></SmartRoute>} />
      <Route path="/subscribe/success" element={<SmartRoute bare><SubscriptionSuccessPage /></SmartRoute>} />
      <Route path="/statistics" element={<SmartRoute><StatisticsPage /></SmartRoute>} />
      <Route path="/receipts" element={<SmartRoute><ReceiptsPage /></SmartRoute>} />
      <Route path="/contabilita" element={<SmartRoute><ContabilitaPage /></SmartRoute>} />
      <Route path="/schedine" element={<SmartRoute><SchedinePage /></SmartRoute>} />
      <Route path="/imposta-soggiorno" element={<SmartRoute><ImpostaSoggiornoPage /></SmartRoute>} />
      <Route path="/ricevute-imposta-soggiorno" element={<SmartRoute><TouristTaxReceiptsPage /></SmartRoute>} />
      <Route path="/pulizie" element={<SmartRoute><CleaningPage /></SmartRoute>} />
      <Route path="/pulizie-app" element={<SmartRoute bare><HostCleaningAppPage /></SmartRoute>} />
      <Route path="/qrcode" element={<SmartRoute><QRCodePage /></SmartRoute>} />
      <Route path="/cleaning/:token" element={<StaffCleaningPage />} />
      <Route path="/eventi" element={<SmartRoute><EventiPage /></SmartRoute>} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useRemoveBadge();
  useEffect(() => {
    initOneSignal();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <CookieBanner />
        <InstallBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
